/**
 * Rutas canónicas del Admin (Shopify-like).
 * Source of truth para paths usado en App, Sidebar, SettingsSidebar, AdminGlobalSearch, etc.
 */

// El canal sale del registro (supabase/functions/_shared/registroDeCanales.ts),
// no de una unión escrita acá: una lista propia sería otra copia de la misma idea.
import type { Canal } from '@/types/canales.generated';

/** Prefijo tipo Shopify: todas las rutas del panel van bajo /store/:storeSlug */
export const STORE_PREFIX = '/store/:storeSlug';

/** Rutas admin (con y sin prefijo store). */
export const ADMIN_PATH_PREFIXES = [
  '/admin',
  '/home',
  '/dashboard',
  '/store',
  '/orders',
  '/products',
  '/customers',
  '/content',
  '/marketing',
  '/discounts',
  '/online-store',
  '/chats',
  '/inboxes',
  '/apps',
  // El módulo Automatización (agentes IA). Sus direcciones viejas redirigen desde RUTAS_MUDADAS
  // y tienen que contar como panel para no pintar la tienda por un instante.
  '/agents',
  '/settings',
  '/sales',
  '/analytics',
  // ADR 0029: las dos raíces del dominio Pedidos. Tienen que estar acá o `main.tsx` carga el CSS
  // de la TIENDA en una pantalla del panel — no falla, se ve roto, que es peor.
  '/ventas',
  '/entregas',
  // La torre de control de Smarty HQ (27-oct-2026).
  '/torre',
  // El tablero del equipo virtual de Smarty HQ (3-nov-2026).
  '/equipo',
  // Rutas viejas: siguen acá mientras redirijan. El redirect ocurre DESPUÉS de elegir el CSS, así
  // que si se sacan de esta lista, entrar por un marcador viejo pinta la tienda por un instante.
  '/transport',
  '/ads',
  '/m',
] as const;

export function isAdminPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (normalized.startsWith('/store/') && normalized.length > 7) return true;
  return ADMIN_PATH_PREFIXES.some(
    (p) => normalized === p || normalized.startsWith(p + '/')
  );
}

/** Construye la ruta del panel con store (ej: /store/mitienda/orders). */
export function storePath(storeSlug: string, path: string): string {
  const segment = path.startsWith('/') ? path.slice(1) : path;
  return segment ? `/store/${encodeURIComponent(storeSlug)}/${segment}` : `/store/${encodeURIComponent(storeSlug)}`;
}

/** Path relativo para rutas anidadas bajo /store/:storeSlug (quita la barra inicial). */
export function storeSegment(absolutePath: string): string {
  return absolutePath.startsWith('/') ? absolutePath.slice(1) : absolutePath;
}

export const ROUTES = {
  // CORE
  HOME: '/home',
  /** Las novedades de Smarty para los negocios de Drop: el muro (plan maestro de novedades). */
  NOVEDADES: '/novedades',
  /** Todas las notificaciones del negocio, con «Cargar más» (la campana muestra las últimas 20). */
  NOTIFICACIONES: '/notificaciones',
  /** Lo que ve Smarty (HQ): la lista con sus métricas, y el editor de una novedad. */
  NOVEDADES_GESTION: '/novedades/gestionar',
  NOVEDADES_NUEVA: '/novedades/gestionar/nueva',
  NOVEDADES_EDITAR: '/novedades/gestionar/:id',
  /** De cero a «un pedido se confirma solo». Cada paso lee el estado real. */
  PRIMEROS_PASOS: '/primeros-pasos',
  CONTROL_CENTER: '/centro-control',
  CONTROL_CENTER_2: '/centro-control-2',
  ORDERS: '/orders',
  ORDERS_ONLINE: '/orders/online',
  /** Detalle de un pedido online — misma ficha que Operaciones pero bajo la sección Pedidos
   *  (sidebar) y limitada a acciones de pedido online (sin venta/entrega/cobranza). */
  ORDERS_ONLINE_DETAIL: '/orders/online/:id',
  ORDERS_ABANDONED: '/orders/abandoned',
  /** Qué hizo el Confirmador con cada pedido, y por qué. Sirve también con el Confirmador apagado. */
  ORDERS_CONFIRMACIONES: '/orders/confirmaciones',
  /** Qué salió solo al courier y por qué. El «por qué» es la pantalla, no un detalle. */
  ORDERS_DESPACHO: '/orders/despacho',
  ORDERS_DESPACHO_CONFIG: '/orders/despacho/configuracion',
  /** Lo que el dueño mira a las 8 de la mañana. */
  ORDERS_PANEL: '/orders/panel',
  ORDERS_ABANDONED_DETAIL: '/orders/abandoned/:id',
  PRODUCTS_ROOT: '/products',
  PRODUCTS: '/products/products',
  PRODUCTS_NEW: '/products/products/new',
  PRODUCTS_EDIT: '/products/products/:id/edit',
  /** Lo que la IA sabe, cita y respeta de este producto. */
  /** 16-sep · La URL vieja de «Para la IA» sigue viva: llega a Venta por WhatsApp, que la absorbió. */
  PRODUCTS_IA: '/products/products/:id/ia',
  PRODUCTS_WHATSAPP: '/products/products/:id/whatsapp',
  PRODUCTS_PHOTO_STUDIO: '/products/photo-studio',
  PRODUCTS_MARKETPLACE_READINESS: '/products/marketplace-readiness',
  PRODUCTS_PHOTO_STUDIO_PROJECT: '/products/photo-studio/:productId',
  PRODUCTS_CATEGORIES: '/products/categories',
  PRODUCTS_CATEGORIES_NEW: '/products/categories/new',
  /** Solo slug: /products/categories/bebidas/edit */
  PRODUCTS_CATEGORIES_EDIT: '/products/categories/:slug/edit',
  PRODUCTS_CATEGORIES_EDIT_LEGACY: '/products/categories/id/:id/edit',
  PRODUCTS_TAGS: '/products/tags',
  PRODUCTS_MODIFIERS: '/products/modifiers',
  PRODUCTS_MODIFIERS_NEW: '/products/modifiers/new',
  /** Solo slug: /products/modifiers/extra-queso/edit (si el segmento es UUID se redirige a slug) */
  PRODUCTS_MODIFIERS_EDIT: '/products/modifiers/:slug/edit',
  PRODUCTS_INVENTORY: '/products/inventory',
  /** Solo slug: /products/inventory/cheesecake-de-fresa (producto se resuelve por slug en DB) */
  PRODUCTS_INVENTORY_PRODUCT: '/products/inventory/:slug',
  /** Legacy: por id (redirige a URL con slug cuando se carga el producto) */
  PRODUCTS_INVENTORY_PRODUCT_LEGACY: '/products/inventory/id/:productId',
  PRODUCTS_VARIANTS: '/products/variants',
  /** El catálogo del WhatsApp Business de cada línea QR, espejado (18-sep-2026). */
  PRODUCTS_WHATSAPP_CATALOG: '/products/whatsapp-catalog',
  PRODUCTS_OFFERS: '/products/offers',
  CUSTOMERS: '/customers',
  /*
   * ⭐ «POR RECOMPRAR» VIVE EN CLIENTES, no en CRM. El founder: «podrías agregar esa opción en el
   *    menú de clientes, creo que ahí serviría mucho». Tiene razón de fondo: es una pregunta sobre
   *    CLIENTES —a quién se le acabó lo que compró—, no sobre una herramienta de CRM. Enterrada en
   *    un módulo aparte, nadie la encontraba: existía desde hace meses y él no sabía que estaba.
   */
  CUSTOMERS_RECOMPRA: '/customers/por-recomprar',
  CUSTOMER_DETAIL: '/customers/:id',
  CONTENT: '/content',

  /** Módulo Dropshipping (proveedores tipo Dropi/Aliclik: catálogo + fulfillment). Sección
   *  de primer nivel: un proveedor de dropshipping NO es un courier — es fuente de productos
   *  Y fulfillment. Ver ADR 0006. */
  DROPSHIPPING: '/dropshipping',
  /** Una página por proveedor (integraciones distintas). El catálogo es un componente compartido
   *  con el proveedor fijo por ruta — sin tabs. La conexión vive en Ajustes → Integraciones. */
  DROPSHIPPING_DROPI: '/dropshipping/dropi',
  DROPSHIPPING_ALICLIK: '/dropshipping/aliclik',
  // La plataforma propia. El slug es de la PLATAFORMA, no de un proveedor: el día que haya cinco
  // proveedores adentro, la URL sigue siendo ésta.
  DROPSHIPPING_SMARTYDROP: '/dropshipping/smartydrop',
  /** Pedidos y pagos de Smarty Drop. Vive bajo el mismo prefijo para que el subnav siga activo.
   *  Una sola pantalla para los dos lados del trato: el revendedor declara lo que pagó y el
   *  proveedor lo confirma. Separarlas duplicaría la misma idea, y el que es proveedor de unos
   *  es revendedor de otros. */
  DROPSHIPPING_SMARTYDROP_PEDIDOS: '/dropshipping/smartydrop/pedidos',
  /** La primera pantalla de un revendedor: qué puede hacer hoy, en orden. El Inicio normal es una
   *  cola de trabajo de una TIENDA, y a una cuenta Drop le queda vacío. */
  DROPSHIPPING_SMARTYDROP_BIENVENIDA: '/dropshipping/smartydrop/empezar',
  /** El saldo con cada proveedor y su historial. Entrada propia del menú: un número que se mira
   *  varias veces por día no puede estar escondido dentro de otra pantalla. */
  DROPSHIPPING_SMARTYDROP_SALDO: '/dropshipping/smartydrop/saldo',
  /** Los cobros que le declararon a este negocio, con su estado. La plataforma ve las recargas;
   *  un proveedor, los pagos de sus pedidos. */
  DROPSHIPPING_SMARTYDROP_COBROS: '/dropshipping/smartydrop/cobros',
  /** Lo que el PROVEEDOR configura de sí mismo: de qué agencia sale, qué cobra por llevarlo. */
  DROPSHIPPING_SMARTYDROP_ALMACEN: '/dropshipping/smartydrop/almacen',
  /** Stock con dueño y lugar, remesas y encargos donde tengo parte. Plan de actores §3. */
  DROPSHIPPING_SMARTYDROP_INVENTARIO: '/dropshipping/smartydrop/inventario',
  /** Ingresos de mercadería: lo que un proveedor manda a guardar a un almacén. Hija de Inventario. */
  DROPSHIPPING_SMARTYDROP_INGRESOS: '/dropshipping/smartydrop/inventario/ingresos',
  /** Encargos donde tengo parte: vendí, es mi producto, o lo despacho. Hija de Inventario. */
  DROPSHIPPING_SMARTYDROP_ENCARGOS: '/dropshipping/smartydrop/inventario/encargos',
  /** El extracto de mis cuentas en el libro de Drop. Plan de actores §5. */
  DROPSHIPPING_SMARTYDROP_LIQUIDACION: '/dropshipping/smartydrop/liquidacion',
  /** La liquidación del drop por día de entrega, con los depósitos del courier (26-oct-2026). */
  DROPSHIPPING_SMARTYDROP_LIQUIDACION_POR_DIA: '/dropshipping/smartydrop/liquidacion/por-dia',
  /** Smarty: las liquidaciones de cada courier, día por día (26-oct-2026): registrar, confirmar o rechazar depósitos. */
  DROPSHIPPING_SMARTYDROP_LIQUIDACIONES_DE_COURIERS: '/dropshipping/smartydrop/liquidaciones-de-couriers',
  /** El courier de Lima: el negocio lo invita; el courier arma su perfil y sus motorizados. */
  DROPSHIPPING_SMARTYDROP_COURIERS: '/dropshipping/smartydrop/couriers',
  /** @deprecated Se conserva y redirige a Dropi para no romper enlaces guardados. */
  DROPSHIPPING_CATALOG: '/dropshipping/catalogo',

  /** Módulo Transporte (couriers, agencias, motorizados) */
  TRANSPORT_AGENCIES: '/transport/agencias',
  TRANSPORT_ROTULOS: '/transport/rotulos',
  TRANSPORT_MOTORIZADOS: '/transport/motorizados',
  /** Detalle de courier bajo el mismo prefijo que la lista (para que el subnav Agencias/Motorizados siga activo). */
  TRANSPORT_AGENCY_COURIER_DETAIL: '/transport/agencias/:courierSlug',
  TRANSPORT_MOTOR_COURIER_DETAIL: '/transport/motorizados/:courierSlug',
  ANALYTICS: '/analytics',
  MARKETING: '/marketing',
  /** Board donde los clientes votan qué se construye (ADR-0017). */
  PROPUESTAS: '/propuestas',
  DISCOUNTS: '/discounts',

  // Agent Platform (Plan §Fase B) — Studio de agentes IA del tenant.
  // Top-level porque "agente" es la unidad de valor que el tenant paga.
  AGENTS: '/agents',
  AGENTS_STUDIO: '/agents/studio',
  AGENTS_STUDIO_DETAIL: '/agents/studio/:agentId',
  /** F6: el lienzo del agente declarativo — la spec dibujada, con su linter de publicación. */
  AGENTS_CONSTRUCTOR: '/agents/constructor',
  /* F5+F6 · La lista de recorridos y el router dibujado. */
  AGENTS_FLUJOS: '/agents/flujos',
  /** Cómo se comporta el Confirmador: quién firma, por qué canal sale, cuándo insiste. */
  AGENTS_CONFIRMADOR: '/agents/flujos/confirmador',
  /** Cómo suena la IA en todos los recorridos. */
  AGENTS_ESTILO: '/agents/estilo',
  /* F1 · El catálogo de campos: lo que el agente guarda de cada cliente y del negocio. */
  AGENTS_CAMPOS: '/agents/campos',
  /*
   * Automatización → Contenido (12-sep-2026): las mismas páginas de Chats (etiquetas, atributos,
   * respuestas rápidas, plantillas) por la puerta del hub, como el «Content» de uChat. Son
   * segundas rutas a la misma pantalla: la de `/chats/...` sigue viva para los marcadores, y el
   * sidebar del módulo se queda en su lugar. Ver `useVolverDelContenido`.
   */
  AGENTS_CONTENIDO_ETIQUETAS: '/agents/contenido/etiquetas',
  // Atributos y Respuestas rápidas volvieron sólo a Chats (13-sep): son del asesor, no del agente.
  AGENTS_CONTENIDO_PLANTILLAS: '/agents/contenido/plantillas',
  AGENTS_CONTENIDO_PLANTILLA_EDITOR: '/agents/contenido/plantillas/:nombre',
  /* Automatización → Intenciones: «cuando el cliente quiere X → flujo». Se listan acá, se editan en el lienzo. */
  AGENTS_INTENCIONES: '/agents/intenciones',
  /** Crear (`nueva`) o editar una intención sin abrir el lienzo. */
  AGENTS_INTENCION_EDITAR: '/agents/intenciones/:nombre',
  /*
   * Difusiones (12-sep-2026): las campañas de WhatsApp salen de Chats y entran al módulo, como en
   * uChat. Lo pidió el founder: «no debe estar en chats». La ruta vieja redirige acá.
   */
  AGENTS_DIFUSIONES: '/agents/difusiones',
  /* F3 · Lo que el agente recuerda de cada persona: los «Bot Users» de uChat. */
  AGENTS_CLIENTES: '/agents/clientes',
  /*
   * 🚨 EL LIENZO ES LA VISTA POR DEFECTO del constructor: `/agents/constructor` ABRE el canvas.
   * La lista es la subruta, no al revés — armar un embudo es una tarea de canvas, y la lista sirve
   * sobre todo para leerlo con números al lado (reportería), que es otra intención.
   */
  /*
   * `/agents/embudo` era el MISMO constructor con otro nombre (veredicto 12-sep §3): hoy redirige a
   * Flujos (ver RUTAS_MUDADAS).
   *
   * Las secciones del plan de benchmark, cada una en su ruta.
   *
   * 🚨 SEPARADAS PORQUE SON TAREAS DISTINTAS, con frecuencias distintas: el embudo se toca al
   * armar, los disparadores al lanzar una campaña, los activos cuando grabás algo nuevo. La
   * primera versión las apiló dentro del constructor y el founder lo cazó mirando el resultado —
   * una página larguísima donde cuatro cosas competían por atención.
   */
  AGENTS_DISPARADORES: '/agents/disparadores',
  AGENTS_PALABRAS_CLAVE: '/agents/palabras-clave',
  AGENTS_DISPARADOR_EDITAR: '/agents/disparadores/:id',
  AGENTS_DISPARADOR_PEDIDO_CREADO: '/agents/disparadores/pedido-creado',
  AGENTS_PALABRA_CLAVE_EDITAR: '/agents/palabras-clave/:id',
  AGENTS_LINK_QR: '/agents/link-qr',
  /** Crear (`nuevo`) o editar un link de campaña: su propia página, como las palabras clave. */
  AGENTS_LINK_EDITAR: '/agents/link-qr/:id',
  AGENTS_ACTIVOS: '/agents/activos',
  AGENTS_HERRAMIENTAS: '/agents/herramientas',
  AGENTS_CATALOG: '/agents/catalog',
  AGENTS_GHOSTWRITER: '/agents/ghostwriter',
  /*
   * F2 · Qué resuelve cada producto y en qué escalón se presenta. Vivía dentro de la ficha de
   * un agente y es del TENANT: la usan todos sus agentes.
   */
  AGENTS_ESTRATEGIA: '/agents/estrategia',
  // Las conversaciones que el examen de resultado corrió contra el agente, como un chat.
  AGENTS_PRUEBAS: '/agents/pruebas',
  AGENTS_KNOWLEDGE_BASE: '/agents/knowledge-base',

  // Smarty Ads — módulo completo (legacy + nuevo chat senior).
  // Plans: docs/plan/PLAN-ADS-META-MODULE.md, PLAN-ADS-AI-STRATEGIST.md,
  //        PLAN-ADS-SALEADS-PARITY.md, PLAN-ADS-CHAT-SENIOR.md
  ADS: '/ads',
  ADS_CHAT: '/ads/chat',
  ADS_CHAT_THREAD: '/ads/chat/:threadId',
  ADS_ONBOARDING: '/ads/onboarding',
  ADS_ONBOARDING_CONVERSATIONAL: '/ads/onboarding/chat',
  ADS_CONNECT: '/ads/connect',
  ADS_CONTENT: '/ads/content',
  ADS_SETTINGS: '/ads/settings',
  ADS_STRATEGIES: '/ads/strategies',
  ADS_STRATEGIES_SAVED: '/ads/strategies/saved',
  ADS_STRATEGY_NEW: '/ads/strategies/new',
  ADS_STRATEGY_DETAIL: '/ads/strategies/:strategyId',
  ADS_TESTIMONIALS: '/ads/testimonials',

  // Sales channels
  ONLINE_STORE: '/online-store',
  ONLINE_STORE_THEMES: '/online-store/themes',
  ONLINE_STORE_THEME_EDITOR: '/online-store/themes/editor',
  ONLINE_STORE_PAGES: '/online-store/pages',
  ONLINE_STORE_PAGE_NEW: '/online-store/pages/nueva',
  ONLINE_STORE_PAGE_EDIT: '/online-store/pages/:pageId',
  ONLINE_STORE_NAVIGATION: '/online-store/navigation',
  ONLINE_STORE_PREFERENCES: '/online-store/preferences',
  ONLINE_STORE_LANDING_PAGES: '/online-store/landing-pages',
  ONLINE_STORE_LANDING_EDITOR: '/online-store/landing-pages/:landingPageId/editor',
  WHATSAPP: '/chats',
  WHATSAPP_INBOX: '/chats/inbox',
  WHATSAPP_CONVERSATION: '/chats/inbox/:conversationId',
  WHATSAPP_MACROS: '/chats/macros',
  /** Las plantillas aprobadas por Meta, con su versión para el número con código QR al lado. */
  WHATSAPP_PLANTILLAS: '/chats/plantillas',
  /** El editor de una plantilla: las dos caras, Cloud con botones y QR con menú numerado. */
  WHATSAPP_PLANTILLA_EDITOR: '/chats/plantillas/:nombre',
  /** El número oficial con un botón, o el QR en un minuto. Los dos conviven. */
  WHATSAPP_CONECTAR: '/chats/conectar',
  /** Los comentarios de Facebook e Instagram, contestados desde la bandeja. */
  WHATSAPP_COMENTARIOS: '/chats/comentarios',
  /** Puertas de entrada: enlaces y QR que llevan al WhatsApp y cuentan el embudo. */
  WHATSAPP_ENTRADAS: '/chats/entradas',
  WHATSAPP_REGLAS: '/chats/reglas',
  WHATSAPP_TEAMS: '/chats/teams',
  WHATSAPP_ASSIGNMENT_RULES: '/chats/assignment-rules',
  WHATSAPP_AUTOMATED_MESSAGES: '/chats/automated-messages',
  WHATSAPP_SUPERVISOR: '/chats/supervisor',
  /** F4: la cola de escalaciones del agente — cada una espera a una persona dentro de su SLA. */
  WHATSAPP_ESCALACIONES: '/chats/escalaciones',
  WHATSAPP_SLA: '/chats/sla',
  WHATSAPP_STATS: '/chats/stats',
  /** F9: conversión del embudo por cohorte semanal (entraron → etapas → compraron). */
  WHATSAPP_EMBUDO: '/chats/embudo',
  WHATSAPP_KNOWLEDGE_BASE: '/chats/knowledge-base',
  WHATSAPP_WEBHOOKS: '/chats/webhooks',
  WHATSAPP_SETTINGS: '/chats/settings',
  WHATSAPP_AGENTS: '/chats/agents',
  WHATSAPP_INBOXES: '/chats/inboxes',

  /**
   * Wizard multi-canal de creación de entrada (Chatwoot-style, future-proof para Web/API/Email).
   * URL canónica bajo `/inboxes/new/*` — no atada a WhatsApp aunque hoy sea el único canal activo.
   */
  /*
   * El asistente vive DENTRO de Chats.
   *
   * 🪤 ANTES ERA `/inboxes/new`, fuera de `/chats/`. El sidebar elige el módulo
   * activo por la ruta, así que crear una entrada mostraba el menú GENERAL en
   * vez del de Chats: uno se perdía a mitad del alta y no había forma de volver
   * al módulo sin salir del asistente.
   *
   * Se arregla moviendo la URL, no agregando `/inboxes/new` a la lista de
   * excepciones del sidebar. La URL tiene que decir dónde estás; una excepción
   * dejaría la mentira intacta y habría que repetirla en cada lugar que decida
   * algo por la ruta.
   *
   * `WHATSAPP_INBOXES` ya es `/chats/inboxes`, así que el sidebar lo reconoce
   * SOLO — no hubo que tocarlo. Esa es la señal de que la ruta estaba mal y no
   * el sidebar.
   */
  NEW_INBOX_ROOT: '/chats/inboxes/new',
  NEW_INBOX_CHANNEL: '/chats/inboxes/new/:channel',
  NEW_INBOX_CONFIGURE: '/chats/inboxes/new/:channel/configure',
  NEW_INBOX_CONNECT: '/chats/inboxes/new/:inboxId/connect',
  NEW_INBOX_COLLABORATORS: '/chats/inboxes/new/:inboxId/collaborators',
  NEW_INBOX_FINISH: '/chats/inboxes/new/:inboxId/finish',
  /** Detalle de entrada (redirige a /connection). */
  WHATSAPP_INBOX_DETAIL: '/chats/inboxes/:inboxId',
  WHATSAPP_INBOX_CONNECTION: '/chats/inboxes/:inboxId/connection',
  WHATSAPP_INBOX_IDENTITY: '/chats/inboxes/:inboxId/identity',
  WHATSAPP_INBOX_COLLABORATORS: '/chats/inboxes/:inboxId/collaborators',
  WHATSAPP_INBOX_AVAILABILITY: '/chats/inboxes/:inboxId/availability',
  WHATSAPP_INBOX_AI: '/chats/inboxes/:inboxId/ai',
  WHATSAPP_INBOX_CSAT: '/chats/inboxes/:inboxId/csat',
  WHATSAPP_INBOX_ROUTING: '/chats/inboxes/:inboxId/routing',
  WHATSAPP_INBOX_ADVANCED: '/chats/inboxes/:inboxId/advanced',
  WHATSAPP_TAGS: '/chats/tags',
  WHATSAPP_CANNED_RESPONSES: '/chats/canned-responses',
  WHATSAPP_CUSTOM_ATTRIBUTES: '/chats/custom-attributes',
  WHATSAPP_AUDIT: '/chats/audit',
  WHATSAPP_CUSTOM_ROLES: '/chats/custom-roles',
  WHATSAPP_CONTACTS: '/chats/contacts',
  WHATSAPP_CONTACT_DETAIL: '/chats/contacts/:contactId',
  WHATSAPP_CAMPAIGNS: '/chats/campaigns',
  WHATSAPP_CSAT: '/chats/csat',
  WHATSAPP_SKILLS: '/chats/skills',

  // Apps (Restaurant Suite)
  APPS_RESTAURANT: '/apps/restaurant',
  APPS_RESTAURANT_POS: '/apps/restaurant/pos',
  APPS_RESTAURANT_CUENTAS: '/apps/restaurant/cuentas',
  APPS_RESTAURANT_KDS: '/apps/restaurant/kds',
  APPS_RESTAURANT_MENU: '/apps/restaurant/menu',
  APPS_RESTAURANT_TABLES: '/apps/restaurant/tables',
  /** R10 · Las reservas de mesa que toma el agente por WhatsApp, por día. */
  APPS_RESTAURANT_RESERVAS: '/apps/restaurant/reservas',

  // Apps (Loyalty)
  APPS_LOYALTY: '/apps/loyalty',
  APPS_LOYALTY_DASHBOARD: '/apps/loyalty',
  APPS_LOYALTY_CUSTOMERS: '/apps/loyalty/customers',
  APPS_LOYALTY_SETTINGS: '/apps/loyalty/settings',
  APPS_LOYALTY_CASHIERS: '/apps/loyalty/cajeros',
  /** Campañas de sello de regalo (una vez por cliente), que la cajera da desde /caja. */
  APPS_LOYALTY_GIFTS: '/apps/loyalty/regalos',
  APPS_LOYALTY_POSTER: '/apps/loyalty/cartel',
  APPS_LOYALTY_QR_SCANNER: '/apps/loyalty/qr-scanner',

  // Apps (Pedidos COD - Releasit-style)
  APPS_COD: '/apps/cod',
  APPS_COD_DESIGN: '/apps/cod/design',
  APPS_COD_SHIPPING: '/apps/cod/shipping',
  APPS_COD_ANALYTICS: '/apps/cod/analytics',
  APPS_COD_SETTINGS: '/apps/cod/settings',
  APPS_COD_PAYMENTS: '/apps/cod/payments',
  APPS_COD_FRAUD: '/apps/cod/fraud',
  // Operaciones v2 (ADR-001 — nuevo dominio de pedidos; coexiste con COD legacy)
  OPS_PEDIDOS: '/ventas/por-confirmar',
  OPS_VENTAS: '/ventas',
  OPS_ORDER_NEW: '/ventas/nuevo',
  OPS_ORDER_DETAIL: '/ventas/orden/:id',
  // Páginas vinculadas al pedido (estilo Odoo: smart buttons → registros vinculados con breadcrumbs)
  OPS_ORDER_ENTREGA: '/ventas/orden/:id/entrega',
  /** F4 · La página «Drop» de una venta de Smarty Drop: lo que se le debe al almacén y el botón de pagar. */
  OPS_ORDER_DROP: '/ventas/orden/:id/drop',
  OPS_ORDER_PAGOS: '/ventas/orden/:id/pagos',
  OPS_ORDER_DOCUMENTOS: '/ventas/orden/:id/documentos',
  OPS_LOGISTICA: '/entregas/por-despachar',
  OPS_COBRANZAS: '/ventas/por-cobrar',
  OPS_CANCELADAS: '/ventas/canceladas',
  // Los pedidos que le entraron al almacén por Smarty Drop. Es un FILTRO: los mismos
  // pedidos siguen apareciendo en Ventas y en Por confirmar.
  OPS_DROP: '/ventas/drop',
  OPS_SETTINGS: '/ventas/configuracion',
  OPS_DASHBOARD: '/ventas/resumen',
  OPS_ENTREGAS_RECAUDO: '/entregas/rendimiento',
  /** Entregas (ADR 0029) — objeto ENVÍO. Raíz propia, no cuelga de /apps/operaciones: es un módulo,
   *  no una pestaña de pedidos. Las rutas viejas de logística redirigen acá. */
  ENTREGAS: '/entregas',
  /** Las novedades de entrega (20261028140000): las visitas fallidas que esperan decisión, por papel. */
  ENTREGAS_NOVEDADES: '/entregas/novedades',
  /** Las guías de despacho del almacén (20261021004300): el lote del día por transporte, armado solo. */
  ENTREGAS_GUIAS: '/entregas/guias',
  /** Lo que el courier recoge: sólo lo que el almacén ya preparó (20261021004300). */
  ENTREGAS_POR_RECOGER: '/entregas/por-recoger',
  /** Las liquidaciones del courier, día por día (26-oct-2026): lo que cobró contra entrega y le deposita a Smarty. */
  ENTREGAS_LIQUIDACIONES: '/entregas/liquidaciones',
  /** Smarty HQ: las ventas de Drop que todavía no pueden llegar a su almacén, y las sin lugar (20261021004300). */
  ENTREGAS_EN_ESPERA: '/entregas/en-espera',
  /** La torre de control de Smarty HQ: lo que pasa en la red, en vivo (20261029000000). */
  TORRE: '/torre',
  /** El 100 % de los paquetes de la red, con su cajón (20261029001000). */
  TORRE_PEDIDOS: '/torre/pedidos',
  /** Lo que espera a alguien en la red: los detectores del plan de operaciones y lo quieto (20261029002000). */
  TORRE_POR_RESOLVER: '/torre/por-resolver',
  /** Dónde está cada paquete: su último responsable y lo que no vuelve al almacén (20261104090000). */
  TORRE_DONDE_ESTA: '/torre/donde-esta',
  /** El tablero del equipo virtual de Smarty HQ: empleados, lo que espera el OK, cronología y metas (20261103390000). */
  EQUIPO: '/equipo',
  APPS_COD_DELIVERY_SUCCESS: '/apps/cod/delivery-success',
  APPS_COD_SALES_BOOSTER: '/apps/cod/sales-booster',
  APPS_COD_BILLING: '/apps/cod/billing',

  // Apps (CRM y Llamadas)
  APPS_CRM: '/apps/crm',
  APPS_CRM_LEADS: '/apps/crm/leads',
  APPS_CRM_PIPELINE: '/apps/crm/pipeline',
  APPS_CRM_DIALER: '/llamadas/dialer',
  APPS_CRM_CALLS: '/llamadas/calls',
  APPS_CRM_CONVERSION: '/apps/crm/conversion',
  /** Cohorte de recompra: clientes a los que se les acabó un producto y no recompraron. */
  APPS_CRM_RECOMPRA: '/apps/crm/recompra',
  /** Copiloto de CRM: chat operativo IA (solo lectura) — novedades, recompra, pendientes. */
  APPS_CRM_COPILOT: '/apps/crm/copilot',
  APPS_CRM_COPILOT_THREAD: '/apps/crm/copilot/:threadId',
  APPS_CRM_SCHEDULE: '/apps/crm/schedule',
  APPS_CRM_AUTOMATIONS: '/apps/crm/automations',
  APPS_CRM_INBOX: '/apps/crm/inbox',
  APPS_CRM_TASKS: '/apps/crm/tasks',
  APPS_CRM_LEAD_DETAIL: '/apps/crm/leads/:id',
  APPS_CRM_DEAL_DETAIL: '/apps/crm/deals/:id',
  APPS_CRM_EMAIL: '/apps/crm/email',
  APPS_CRM_EMAIL_TEMPLATES: '/apps/crm/email/templates',
  APPS_CRM_SEQUENCES: '/apps/crm/sequences',
  APPS_CRM_SEQUENCE_DETAIL: '/apps/crm/sequences/:id',
  APPS_CRM_DASHBOARD: '/apps/crm/dashboard',
  APPS_CRM_MERGE: '/apps/crm/merge',
  APPS_CALL_CENTER: '/llamadas',
  APPS_CALL_CENTER_SETTINGS: '/llamadas/settings',
  APPS_CALL_CENTER_STATS: '/llamadas/stats',
  APPS_CALL_CENTER_EXTENSIONS: '/llamadas/extensions',
  APPS_CALL_CENTER_CAMPAIGNS: '/llamadas/campaigns',
  APPS_CALL_CENTER_CAMPAIGN_DETAIL: '/llamadas/campaigns/:id',
  APPS_CALL_CENTER_SUPERVISOR: '/llamadas/supervisor',
  APPS_CALL_CENTER_CART_RECOVERY: '/llamadas/cart-recovery',
  APPS_CALL_CENTER_POST_PURCHASE: '/llamadas/post-purchase',
  APPS_CALL_CENTER_COD_CONFIRMATION: '/llamadas/cod-confirmation',
  APPS_CALL_CENTER_COLLECTION: '/llamadas/collection',
  APPS_CALL_CENTER_VOICE_METRICS: '/llamadas/voice-metrics',
  APPS_CALL_CENTER_CALL_ANALYSIS: '/llamadas/call-analysis/:crmCallId',
  APPS_CALL_CENTER_CALL_ANALYSIS_LIST: '/llamadas/test-calls',
  APPS_CALL_CENTER_VOICE_SETTINGS: '/llamadas/voice-settings',

  // Voice AI
  APPS_CRM_VOICE_AI: '/llamadas/voice-ai',
  APPS_CRM_CALL_COACHING: '/llamadas/call-coaching',

  // Report Builder
  APPS_REPORTS: '/apps/reports',
  APPS_REPORTS_DASHBOARDS: '/apps/reports/dashboards',
  APPS_REPORTS_DASHBOARD_DETAIL: '/apps/reports/dashboards/:dashboardId',
  APPS_REPORTS_SAVED: '/apps/reports/saved',

  // Workflow Builder
  APPS_WORKFLOWS: '/apps/workflows',
  APPS_WORKFLOW_DETAIL: '/apps/workflows/:workflowId',

  // Ticketing / Service Desk
  APPS_TICKETS: '/apps/tickets',
  APPS_TICKET_DETAIL: '/apps/tickets/:ticketId',

  // Chatbot Builder
  APPS_CHATBOT: '/apps/chatbot',
  APPS_CHATBOT_EDITOR: '/apps/chatbot/:chatbotId/editor',

  // Apps (Impuestos — "Smarty Impuestos": conexión SUNAT/SIRE, IGV en tiempo real)
  // Plan: docs/plan/PLAN-APP-IMPUESTOS-SUNAT-SIRE.md
  // Contabilidad — módulo unificado: facturación electrónica (CPE) + impuestos (SIRE)
  APPS_CONTABILIDAD: '/apps/contabilidad',                            // resumen fiscal (IGV/SIRE)
  APPS_CONTABILIDAD_COMPROBANTES: '/apps/contabilidad/comprobantes',  // CPE emitidos
  APPS_CONTABILIDAD_COMPROBANTE: '/apps/contabilidad/comprobantes/:id', // detalle de un CPE
  APPS_CONTABILIDAD_CONFIG: '/apps/contabilidad/configuracion',       // conexión SUNAT + emisor + certificado
  // Rutas legadas — redirigen a las nuevas (compatibilidad de bookmarks).
  APPS_IMPUESTOS: '/apps/impuestos',
  APPS_IMPUESTOS_CONEXION: '/apps/impuestos/conexion',
  APPS_CONTABILIDAD_CERT: '/apps/contabilidad/certificado',

  /*
   * Las nueve constantes `MOBILE_*` (`/m/home`, `/m/leads`…) se borraron el 07-sep-2026 al retirar
   * la segunda interfaz móvil. No las usaba nadie más que su propio bloque de rutas: el panel es
   * ahora la única superficie, y responde en cualquier ancho. Las redirecciones de compatibilidad
   * viven en AppRoutes.tsx y se borran en 1-2 releases.
   */

  // Table QR (Autopedido)
  TABLE_QR: '/table/:qrToken',
  TABLE_QR_MENU: '/table/:qrToken/menu',
  TABLE_QR_ORDER_CONFIRMATION: '/table/:qrToken/order/:orderId/confirmation',
  TABLE_QR_ORDER_TRACKING: '/table/:qrToken/order/:orderId',

  // Settings
  SETTINGS: '/settings',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_BUSINESS: '/settings/business',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_USERS_ROLES: '/settings/users/roles',
  SETTINGS_USERS_SECURITY: '/settings/users/security',
  SETTINGS_PAYMENTS: '/settings/payments',
  SETTINGS_PAYMENT_PROVIDERS: '/settings/payments/providers',
  SETTINGS_CHECKOUT: '/settings/checkout',
  SETTINGS_SHIPPING: '/settings/shipping-and-delivery',
  SETTINGS_DELIVERY_METHODS: '/settings/shipping-and-delivery/delivery-methods',
  SETTINGS_DELIVERY_RULES: '/settings/shipping-and-delivery/delivery-rules',
  SETTINGS_LOGISTICS_COURIER_RULES: '/settings/shipping-and-delivery/courier-rules',
  SETTINGS_LOGISTICS_COURIER_DETAIL: '/settings/shipping-and-delivery/couriers/:courierSlug',
  SETTINGS_SHALOM_AGENCIES: '/settings/shipping-and-delivery/shalom-agencies',
  SETTINGS_SHIPPING_METHODS: '/settings/shipping-and-delivery/shipping-methods',
  SETTINGS_DOMAINS: '/settings/domains',
  SETTINGS_DOMAINS_CONFIGURE: '/settings/domains/configure/:id',
  SETTINGS_BRAND: '/settings/brand',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  /** Notificaciones a grupos de WhatsApp por evento (handoff, reclamo, ventas, leads). */
  SETTINGS_NOTIFICATIONS_WHATSAPP: '/settings/whatsapp-notifications',
  /** Módulo Avisos: la misma pantalla, pero con puerta propia en el menú. */
  AVISOS: '/avisos',
  /** Reglas de contacto con el CLIENTE (silencio, tope, no-contactar). ADR-0011. */
  SETTINGS_CONTACT_POLICY: '/settings/contacto-clientes',
  SETTINGS_CALL_CENTER: '/settings/call-center',
  SETTINGS_APPS_AND_SALES_CHANNELS: '/settings/apps-and-sales-channels',
  /** Hub de Integraciones (índice de conexión de servicios externos). */
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  /** Detalle/conexión de Shopify (pedidos en tiempo real → Operaciones). */
  SETTINGS_INTEGRATIONS_SHOPIFY: '/settings/integrations/shopify',
  /** Detalle/conexión de Mercado Libre (canal de venta: pedidos + publicar productos). */
  SETTINGS_INTEGRATIONS_MERCADOLIBRE: '/settings/integrations/mercadolibre',
  SETTINGS_INTEGRATIONS_FALABELLA: '/settings/integrations/falabella',
  SETTINGS_INTEGRATIONS_ODOO: '/settings/integrations/odoo',
  /** Preview de homologación de productos de Odoo (vincular/crear/omitir) antes de migrar pedidos. */
  SETTINGS_INTEGRATIONS_ODOO_PRODUCTS: '/settings/integrations/odoo/productos',
  /** Control de la migración masiva de pedidos de Odoo (preview + progreso + pausa). */
  SETTINGS_INTEGRATIONS_ODOO_MIGRATE: '/settings/integrations/odoo/migrar',
  /** Vincular vendedores de Odoo con usuarios de Smarty (atribución de ventas). */
  SETTINGS_INTEGRATIONS_ODOO_SELLERS: '/settings/integrations/odoo/vendedores',
  /** Conexión por proveedor de IA — cada uno su propia página (como Shopify/Odoo/couriers).
   *  Componente compartido con el proveedor fijo por ruta. BYO keys cifradas. Ver ADR 0007. */
  SETTINGS_INTEGRATIONS_OPENAI: '/settings/integrations/openai',
  SETTINGS_INTEGRATIONS_MCP: '/settings/integrations/mcp',
  SETTINGS_INTEGRATIONS_ANTHROPIC: '/settings/integrations/anthropic',
  SETTINGS_INTEGRATIONS_GEMINI: '/settings/integrations/gemini',
  /** @deprecated Página unificada de llaves de IA; ahora cada proveedor tiene la suya. Redirige a OpenAI. */
  SETTINGS_INTEGRATIONS_AI: '/settings/integrations/ia',
  /** Conexión por proveedor de dropshipping — cada uno su propia página (como Shopify/Odoo).
   *  Componente compartido con el proveedor fijo por ruta. Ver ADR 0006/0008. */
  SETTINGS_INTEGRATIONS_DROPI: '/settings/integrations/dropi',
  SETTINGS_INTEGRATIONS_ALICLIK: '/settings/integrations/aliclik',
  /** @deprecated Página unificada de dropshipping; ahora cada proveedor tiene la suya. Redirige a Dropi. */
  SETTINGS_INTEGRATIONS_DROPSHIPPING: '/settings/integrations/dropshipping',
  /** Conexión de couriers — la CONEXIÓN vive en Integraciones (una página por courier, como Shopify/Odoo).
   *  La configuración operativa (agencias, defaults) sigue en Transporte. */
  SETTINGS_INTEGRATIONS_SHALOM: '/settings/integrations/shalom',
  SETTINGS_INTEGRATIONS_EVA: '/settings/integrations/eva',
  SETTINGS_INTEGRATIONS_OLVA: '/settings/integrations/olva',
  /** Hub del canal Marketplaces (sidebar → Canales): estado + configuración general. */
  MARKETPLACES: '/marketplaces',
  /**
   * La preparación del catálogo para publicar, ahora bajo su canal.
   *
   * 🪤 LA DIRECCIÓN VIEJA (`/products/marketplace-readiness`) SIGUE VIVA como alias. Mover una
   * pantalla de módulo no puede romperle el enlace guardado a nadie: el que la tenga en favoritos
   * o pegada en un mensaje llega igual.
   */
  MARKETPLACES_READINESS: '/marketplaces/preparacion',

  /** Rutas de la tienda pública (storefront). Usar estas en el tema para que los enlaces no apunten al admin. */
  STORE_HOME: '/',
  STORE_PRODUCT: '/productos/:id',
  STORE_CART: '/checkout',
  STORE_MENU: '/pedir',
  STORE_PAGE: '/paginas/:slug',
  STORE_ACCOUNT: '/mi-cuenta',
  STORE_PRIVACY: '/paginas/privacidad',
  STORE_TERMS: '/paginas/terminos',
  STORE_SHIPPING: '/paginas/envios',
  STORE_RETURNS: '/paginas/devoluciones',
  STORE_LANDING: '/l/:slug',

  // Legacy / compat (aún usado en algunos lugares)
  SALES_ORDERS: '/sales/orders',
  SALES_HEATMAP: '/sales/heatmap',
  SALES_RANKING: '/sales/ranking',
  SIMULADOR_RENTABILIDAD: '/rentabilidad',
  FINANZAS_PNL: '/finanzas',
  COMISIONES: '/comisiones',
  REPORTES: '/reportes',

  // Settings placeholders (Shopify-like)
  SETTINGS_PLAN: '/settings/plan',
  SETTINGS_BILLING: '/settings/billing',
  SETTINGS_TAXES_AND_DUTIES: '/settings/taxes-and-duties',
  SETTINGS_LOCATIONS: '/settings/locations',
  SETTINGS_CUSTOM_DATA: '/settings/custom-data',
  SETTINGS_LANGUAGES: '/settings/languages',
  SETTINGS_POLICIES: '/settings/policies',
} as const;

type _RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * ADR 0029 §5.1 — las direcciones que el dominio Pedidos dejó atrás.
 *
 * «Operaciones» era un contenedor abstracto y sus pantallas colgaban de `/apps/`, como si el núcleo
 * del negocio fuera un accesorio instalado. Ahora hay dos raíces, cada una con su objeto: `/ventas`
 * (el pedido) y `/entregas` (el envío).
 *
 * 🪤 ESTAS URLS ESTÁN EN MARCADORES Y PEGADAS EN CHATS INTERNOS. Ninguna puede quedar en 404, y el
 *    redirect es permanente: no es una ventana de transición, es la dirección vieja para siempre.
 *
 * Son reglas de PREFIJO, no de ruta exacta: `LegacyRouteRedirect` reescribe el comienzo del
 * pathname y conserva lo que sigue. Por eso una sola regla cubre `/orden/:id` y sus tres pestañas,
 * y por eso el link a un pedido puntual —el que se pega en el chat interno— sigue abriendo ESE
 * pedido y no la lista.
 *
 * La regla que ordena el mapa: **una COLA es una ruta; una VISTA es un `?vista=`**. Las colas son
 * mutuamente excluyentes (un pedido está en una y solo una), así que merecen dirección propia; las
 * vistas se solapan a propósito y son un filtro dentro de la cola. Ver `src/lib/operations/queues.ts`.
 */
export const RUTAS_MUDADAS: Readonly<Record<string, string>> = {
  // Ventas — el objeto es el PEDIDO
  '/apps/operaciones/pedidos': ROUTES.OPS_PEDIDOS,
  '/apps/operaciones/ventas': ROUTES.OPS_VENTAS,
  // «Ventas 2» se fundió en Ventas cuando agrupar pasó a ser una opción estándar. La URL sobrevive
  // solo como marcador de alguien.
  '/apps/operaciones/ventas-2': ROUTES.OPS_VENTAS,
  '/apps/operaciones/cobranzas': ROUTES.OPS_COBRANZAS,
  '/apps/operaciones/canceladas': ROUTES.OPS_CANCELADAS,
  '/apps/operaciones/nuevo-pedido': ROUTES.OPS_ORDER_NEW,
  // Prefijo a propósito: cubre `/orden/:id` y sus pestañas (entrega, pagos, documentos) con una regla.
  '/apps/operaciones/orden': '/ventas/orden',
  '/apps/operaciones/resumen': ROUTES.OPS_DASHBOARD,
  '/apps/operaciones/configuracion': ROUTES.OPS_SETTINGS,

  // Entregas — el objeto es el ENVÍO
  //
  // 🪤 `logistica` NO es una equivalencia exacta, y por eso se documenta acá: la pantalla vieja
  //    listaba PEDIDOS filtrados por estado de envío. Se manda a «Por despachar», que es el trabajo
  //    que esa gente venía a hacer.
  '/apps/operaciones/logistica': ROUTES.OPS_LOGISTICA,
  '/apps/operaciones/entregas-recaudo': ROUTES.OPS_ENTREGAS_RECAUDO,

  /*
   * Chats — el asistente de alta vivía FUERA del módulo.
   *
   * 🪤 `/inboxes/new` no empezaba con `/chats/`, y el sidebar elige el módulo
   * activo por la ruta: crear una entrada mostraba el menú GENERAL en vez del de
   * Chats, así que uno se perdía a mitad del alta.
   *
   * Se arregló moviendo la URL y no agregando una excepción al sidebar: la URL
   * tiene que decir dónde estás. `WHATSAPP_INBOXES` ya es `/chats/inboxes`, así
   * que el sidebar lo reconoció solo — sin tocarlo. Esa fue la señal de que lo
   * que estaba mal era la ruta.
   *
   * El prefijo cubre los cinco pasos del asistente con una sola regla.
   */
  '/inboxes/new': ROUTES.NEW_INBOX_ROOT,
  /*
   * Automatización — lo que sobraba del sidebar (veredicto 12-sep §3). Las direcciones viven como
   * marcador; lo que había detrás vive en otro lado:
   *   · el embudo era el mismo constructor que abre Flujos;
   *   · Resultados era la misma tabla que El agente › Rendimiento;
   *   · Pulgares abajo tenía 0 votos en la historia; la cola de calidad es la del agente;
   *   · Atributos y Respuestas rápidas son del asesor y volvieron sólo a Chats;
   *   · «Bots» de Chats era un placeholder vacío; lo más cercano es la asignación automática.
   */
  '/agents/embudo': ROUTES.AGENTS_FLUJOS,
  // Cerebro se fundió: lo por aprobar está en Base de conocimiento; los números, en El agente › Rendimiento.
  '/agents/cerebro': ROUTES.AGENTS_KNOWLEDGE_BASE,
  '/agents/outcomes': ROUTES.AGENTS_STUDIO,
  '/agents/pulgares': ROUTES.AGENTS_STUDIO,
  '/agents/contenido/atributos': ROUTES.WHATSAPP_CUSTOM_ATTRIBUTES,
  '/agents/contenido/respuestas-rapidas': ROUTES.WHATSAPP_CANNED_RESPONSES,
  '/chats/bots': ROUTES.WHATSAPP_ASSIGNMENT_RULES,
};

/**
 * Prefijos de rutas "workspace" que activan el sidebar en modo rail (56px, solo íconos).
 * Son páginas inmersivas (inbox, pipeline, dialer) que necesitan máximo viewport.
 */
export const WORKSPACE_ROUTE_PREFIXES = [
  '/chats/inbox',
  '/agents/constructor',        // el canvas de un embudo real no entra en una columna

  '/apps/crm/inbox',
  '/apps/crm/pipeline',
  '/apps/crm/deals',       // detalle de deal usa panel lateral
] as const;

/** Verifica si un pathname corresponde a una ruta workspace (auto-rail). */
export function isWorkspaceRoute(pathname: string): boolean {
  // Quitar prefijo /store/:slug si existe
  const stripped = pathname.replace(/^\/store\/[^/]+/, '');
  return WORKSPACE_ROUTE_PREFIXES.some(
    (prefix) => stripped === prefix || stripped.startsWith(prefix + '/')
  );
}

/** Convierte nombre de producto a segmento URL (minúsculas, guiones, sin acentos). */
export function slugifyProductName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'producto';
}

/** Slug para modificadores (misma lógica que slugifyProductName). */
export function slugifyModifierName(name: string): string {
  const s = slugifyProductName(name);
  return s || 'modificador';
}

/** Slug para courier en URL (nombre → minúsculas, guiones). Si la columna slug existe en DB se puede usar esa; si no, se deriva del nombre. */
export function slugifyCourierName(name: string): string {
  const s = slugifyProductName(name);
  return s || 'courier';
}

/**
 * Funciones helper para construir rutas dinámicas con parámetros
 */
export const routeHelpers = {
  /**
   * Construye la ruta de edición de producto con el ID
   */
  productEdit: (id: string | number): string => {
    return ROUTES.PRODUCTS_EDIT.replace(':id', String(id));
  },

  /**
   * Construye la ruta de edición de categoría por slug (URL sin UUID)
   */
  categoryEdit: (slug: string): string => {
    return `/products/categories/${encodeURIComponent(slug)}/edit`;
  },

  /**
   * Detalle de inventario (entradas/salidas) de un producto.
   * Solo slug en la URL: /products/inventory/cheesecake-de-fresa
   */
  productInventoryDetail: (slug: string): string => {
    return `/products/inventory/${encodeURIComponent(slug)}`;
  },

  /**
   * Editar modificador por slug (URL legible)
   */
  modifierEdit: (slug: string): string => {
    return `/products/modifiers/${encodeURIComponent(slug)}/edit`;
  },

  /** Detalle de entrada WhatsApp por id, sección opcional (connection por default). */
  whatsappInboxDetail: (
    inboxId: string,
    section:
      | 'connection'
      | 'identity'
      | 'collaborators'
      | 'availability'
      | 'ai'
      | 'csat'
      | 'routing'
      | 'conversions'
      | 'advanced' = 'connection',
  ): string => `/chats/inboxes/${encodeURIComponent(inboxId)}/${section}`,

  /** Rutas del wizard de creación de entrada multi-canal. */
  newInboxChannel: (channel: Canal): string =>
    `/chats/inboxes/new/${channel}`,
  newInboxConfigure: (channel: Canal, provider?: string): string =>
    `/chats/inboxes/new/${channel}/configure${provider ? `?p=${encodeURIComponent(provider)}` : ''}`,
  newInboxConnect: (inboxId: string): string =>
    `/chats/inboxes/new/${encodeURIComponent(inboxId)}/connect`,
  newInboxCollaborators: (inboxId: string): string =>
    `/chats/inboxes/new/${encodeURIComponent(inboxId)}/collaborators`,
  newInboxFinish: (inboxId: string): string =>
    `/chats/inboxes/new/${encodeURIComponent(inboxId)}/finish`,

  /** URL de producto en la tienda pública */
  storeProduct: (id: string): string => `/productos/${id}`,
  /** URL de página de contenido en la tienda */
  storePage: (slug: string): string => `/paginas/${slug}`,

  /** Detalle de courier por slug (URL legible: /settings/shipping-and-delivery/couriers/shalom) */
  courierDetail: (slug: string): string =>
    `/settings/shipping-and-delivery/couriers/${encodeURIComponent(slug)}`,

  /** Detalle desde Transporte → Agencias (URL bajo /transport/agencias/…). */
  transportAgencyCourierDetail: (slug: string): string =>
    `/transport/agencias/${encodeURIComponent(slug)}`,

  /** Detalle desde Transporte → Motorizados. */
  transportMotorCourierDetail: (slug: string): string =>
    `/transport/motorizados/${encodeURIComponent(slug)}`,

  /** Agent Platform — Detalle del agente IA del tenant. Plan §Fase B.2. */
  agentStudioDetail: (agentId: string): string =>
    `/agents/studio/${encodeURIComponent(agentId)}`,
};

